import React, {
  createContext,
  useContext,
  useState,
  type ReactNode,
} from "react";

import type { AxiosResponse } from "axios";
import axios from "axios";

import type {
  SisenseDashboardCategoryRetrieve,
  SisenseDashboardRetrieve,
  SisenseDashboardsList200Response,
} from "~/src/types";

interface DashboardState {
  data: SisenseDashboardRetrieve[];
  loading: boolean;
  error: string | null;
}

interface DashboardsByCategoryState {
  data: ({ category: SisenseDashboardCategoryRetrieve } & {
    dashboards: SisenseDashboardRetrieve[];
  })[];
  loading: boolean;
  error: string | null;
}

interface DashboardContextType {
  loadDashboards: (queryParameters?: string) => void;
  loadDashboardsByCategory: (queryParameters?: string) => void;
  allDashboards: DashboardState;
  dashboardsByCategory: DashboardsByCategoryState;
  updateDashboard: (dashboard: SisenseDashboardRetrieve) => void;
  updateCategoryOrder: (categoryUuid: string, dashboardIds: string[]) => void;
  removeDashboard: (dashboardUuid: string) => void;
}

const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined,
);

interface DashboardProviderProps {
  children: ReactNode;
}

export const DashboardProvider = ({ children }: DashboardProviderProps) => {
  const [allDashboards, setAllDashboards] = useState<DashboardState>({
    data: [],
    loading: true,
    error: null,
  });
  const [dashboardsByCategory, setDashboardsByCategory] =
    useState<DashboardsByCategoryState>({
      data: [],
      loading: true,
      error: null,
    });

  const loadDashboards = (queryParameters?: string) => {
    setAllDashboards((prev) => {
      return { ...prev, loading: true };
    });

    axios
      .get(`sisense/dashboards${queryParameters ? `${queryParameters}` : ""}`)
      .then((res: AxiosResponse<SisenseDashboardsList200Response>) => {
        setAllDashboards({
          data:
            (res.data.result?.sort((a, b) => {
              const nameA = a.category?.name ?? "";
              const nameB = b.category?.name ?? "";
              return nameA.localeCompare(nameB);
            }) as SisenseDashboardRetrieve[]) ?? [],
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        let errorMessage = "Failed to load dashboards";
        if (
          err.response?.data?.result?.error ===
          "User does not have a Sisense account or is not authorized"
        ) {
          console.error("User does not have access to Sisense:", err);
          errorMessage =
            "Your account does not have access to Insights Hub. Please contact your administrator.";
        } else {
          console.error("Error fetching dashboards:", err);
        }

        setAllDashboards({
          data: [],
          loading: false,
          error: errorMessage,
        });
      });
  };

  const loadDashboardsByCategory = (queryParameters?: string) => {
    setDashboardsByCategory((prev) => {
      return { ...prev, loading: true };
    });

    axios
      .get(
        `sisense/dashboards/dashboards_by_category${queryParameters ? `${queryParameters}` : ""}`,
      )
      .then((res) => {
        setDashboardsByCategory({
          data: res.data.result,
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        console.error("Error fetching dashboards by category:", err);

        setDashboardsByCategory({
          data: [],
          loading: false,
          error: "Failed to load dashboards by category",
        });
      });
  };

  const updateDashboard = (updatedDashboard: SisenseDashboardRetrieve) => {
    setAllDashboards((prev) => {
      return {
        ...prev,
        data: prev.data.map((dashboard) =>
          dashboard.uuid === updatedDashboard.uuid
            ? updatedDashboard
            : dashboard,
        ),
      };
    });

    setDashboardsByCategory((prev) => {
      return {
        ...prev,
        data: prev.data.map((category) => {
          return {
            ...category,
            dashboards: category.dashboards.map((dashboard) =>
              dashboard.uuid === updatedDashboard.uuid
                ? updatedDashboard
                : dashboard,
            ),
          };
        }),
      };
    });
  };

  const removeDashboard = (dashboardUuid: string) => {
    setAllDashboards((prev) => {
      return {
        ...prev,
        data: prev.data.filter((dashboard) => dashboard.uuid !== dashboardUuid),
      };
    });
  };

  const updateCategoryOrder = (
    categoryUuid: string,
    dashboardIds: string[],
  ) => {
    const orderedCategories = dashboardsByCategory.data.map((category) => {
      if (category.category.uuid === categoryUuid) {
        return {
          ...category,
          dashboards: category.dashboards
            .sort(
              (a, b) =>
                dashboardIds.indexOf(a.uuid ?? "") -
                dashboardIds.indexOf(b.uuid ?? ""),
            )
            .map((dashboard, index) => {
              return {
                ...dashboard,
                rank: index + 1,
              };
            }),
        };
      }
      return category;
    });

    setDashboardsByCategory((prev) => {
      return {
        ...prev,
        data: orderedCategories,
      };
    });
  };

  const value: DashboardContextType = {
    loadDashboards,
    loadDashboardsByCategory,
    allDashboards,
    dashboardsByCategory,
    updateDashboard,
    updateCategoryOrder,
    removeDashboard,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = (): DashboardContextType => {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error(
      "useDashboardContext must be used within a DashboardProvider",
    );
  }
  return context;
};
